import { Button, Input, InputLabel } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "store/hooks";
import ProfileSubpageHeader from "../ProfileSubpageHeader/ProfileSubpageHeader";
import "./CompanyDetailsPage.scss";
import {
  APIGetOrganization,
  APIUpdateOrganization,
  AVATARGetOrgImageUrl,
  AVATARUpdateOrgAvatarBanner,
} from "utils/api";
import LoadingImage from "components/LoadingImage";
import ProfileAvatar from "components/ProfileAvatar";
import ImageCropProvider from "components/Cropper/ImageCropProvider";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import config from "utils/config";

const CompanyDetailsPage = () => {
  const { t } = useTranslation();
  const selectedOrgId = useAppSelector((state) => state.auth.selectedOrganizationId);

  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [defaultLanguage, setDefaultLanguage] = useState("");
  const [invoicingName, setInvoicingName] = useState("");
  const [invoicingAddress, setInvoicingAddress] = useState("");
  const [invoicingZip, setInvoicingZip] = useState("");
  const [invoicingCity, setInvoicingCity] = useState("");
  const [invoicingCountry, setInvoicingCountry] = useState("FR");
  const [invoicingVATCode, setInvoicingVATCode] = useState("");
  const [croppedImageFile, setCroppedImageFile] = useState<File | null>(null);
  const [croppedBannerFile, setCroppedBannerFile] = useState<File | null>(null);
  const [isAvatarLoading, setIsAvatarLoading] = useState(false);
  const [isBannerLoading, setIsBannerLoading] = useState(false);
  const [avatarError, setAvatarError] = useState("");
  const [bannerError, setBannerError] = useState("");


  const all_countries = useMemo<string[]>(() => config.countries, []);

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");

  const { data: organizationData, isLoading: isOrgLoading, error: orgError, refetch: refetchOrg } = useQuery<any, Error>({
    queryKey: ["organization", selectedOrgId],
    queryFn: () => {
      if (!selectedOrgId) return Promise.reject(new Error("No organization ID selected"));
      return APIGetOrganization(selectedOrgId);
    },
    enabled: !!selectedOrgId,
    select: (res) => res.data,
  });

  useEffect(() => {
    if (organizationData) {
      setName(organizationData.name || "");
      setContactEmail(organizationData.contact_email || "");
      setDefaultLanguage(organizationData.default_language || "");
      setInvoicingName(organizationData.invoicing_name || "");
      setInvoicingAddress(organizationData.invoicing_address || "");
      setInvoicingZip(organizationData.invoicing_zip || "");
      setInvoicingCity(organizationData.invoicing_city || "");
      setInvoicingCountry(organizationData.invoicing_country || "");
      setInvoicingVATCode(organizationData.invoicing_vat_code || "");
    }
  }, [organizationData]);

  useEffect(() => {
    if (selectedOrgId) {
      refetchOrg();
    }
  }, [selectedOrgId, refetchOrg]);


  const updateOrganizationMutation = useMutation({
    mutationFn: (updatedData: any) => {
      if (!selectedOrgId) throw new Error("No organization selected");
      const { avatar, ...dataToUpdate } = updatedData;
      return APIUpdateOrganization(selectedOrgId, dataToUpdate);
    },
    onSuccess: () => {
      setSubmissionSuccess(true);
      setSubmissionError("");
      setIsSubmitLoading(false);
      refetchOrg();
    },
    onError: (error: Error) => {
      console.error("Update failed:", error);
      setSubmissionError(t("something-went-wrong-please-try-again"));
      setSubmissionSuccess(false);
      setIsSubmitLoading(false);
    },
  });

  // --- Avatar Upload Handler ---
  const handleUploadAvatar = async () => {
    if (croppedImageFile && selectedOrgId) {
      setAvatarError("");
      setIsAvatarLoading(true);
      try {
        await AVATARUpdateOrgAvatarBanner(selectedOrgId, croppedImageFile, false);
        await new Promise(resolve => setTimeout(resolve, 1000));

        setAvatarError("");
        setCroppedImageFile(null);
        refetchOrg();
      } catch (err) {
        console.error("Avatar upload failed:", err);
        setAvatarError(t("something-went-wrong-please-try-again"));
      } finally {
        setIsAvatarLoading(false);
      }
    }
  };

  const handleUploadBanner = async () => {
    if (croppedBannerFile && selectedOrgId) {
      setBannerError("");
      setIsBannerLoading(true);
      try {
        await AVATARUpdateOrgAvatarBanner(selectedOrgId, croppedBannerFile, true);
        await new Promise(resolve => setTimeout(resolve, 1000));

        setBannerError("");
        setCroppedBannerFile(null);
        refetchOrg();
      } catch (err) {
        console.error("Banner upload failed:", err);
        setBannerError(t("something-went-wrong-please-try-again"));
      } finally {
        setIsBannerLoading(false);
      }
    }
  };

  // --- Validation ---
  const validateEmailOnChange = (new_email: string): boolean => {
    const emailRegex = new RegExp(
      "^[a-zA-Z0-9.!#$%&'*+\\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]" +
      "{0,61}[a-zA-Z0-9])?(?:\\.[a-zA-Z0-9](?:[a-zA-Z0-9-]" +
      "{0,61}[a-zA-Z0-9])?)*$",
    );
    setContactEmail(new_email); // Update contactEmail state
    if (
      new_email &&
      (new_email.length < 3 ||
        new_email.length > 254 ||
        !emailRegex.test(new_email))
    ) {
      setEmailError(t("invalid-email"));
      return false;
    }
    setEmailError("");
    return true;
  };

  // --- Submit Handler ---
  const handleSubmit = () => {
    setSubmissionSuccess(false);
    setNameError(""); // Use nameError
    setEmailError("");
    setSubmissionError("");

    // Validate Company Name
    if (!name.trim()) {
      setNameError(t("company-name-cannot-be-empty")); // Use specific translation
      return;
    }
    // Validate Contact Email
    if (contactEmail && !validateEmailOnChange(contactEmail)) {
      // Error is set within validateEmailOnChange
      return;
    }

    setIsSubmitLoading(true);

    // Prepare payload with only the fields from the form
    const updatedData: any = { // Using any as requested
      name: name.trim(),
      contact_email: contactEmail.trim(),
      default_language: defaultLanguage,
      invoicing_name: invoicingName.trim(),
      invoicing_address: invoicingAddress.trim(),
      invoicing_zip: invoicingZip.trim(),
      invoicing_city: invoicingCity.trim(),
      invoicing_country: invoicingCountry,
      invoicing_vat_code: invoicingVATCode.trim(),
      // Avatar is handled by handleUploadAvatar
    };

    updateOrganizationMutation.mutate(updatedData);
  };


  // --- Render Logic ---
  if (isOrgLoading) {
    return <LoadingImage />;
  }

  if (orgError) {
    // Provide more specific error feedback if possible
    return <div className="error-message">{t("error-loading-organization-data")}: {orgError.message}</div>;
  }

  // Handle case where user might not have orgs or none is selected
  if (!selectedOrgId && !isOrgLoading) {
    return <div></div>
    // return <div className="info-message">{t("no-organization-selected")}</div>;
  }

  if (!organizationData && !isOrgLoading && selectedOrgId) {
    return <div className="info-message">{t("organization-not-found")}</div>;
  }


  return (
    <section className="profile-personal-details company-details">
      <>
        <ProfileSubpageHeader
          title={t("company-information")}
          description={t("update-your-company-information-here")}
        />

        <div className="xl:inline-flex xl:gap-[90px]">
          <div className="flex flex-col gap-[30px] items-center justify-center xl:hidden w-full">
            <h4 className="mt-6 w-full text-lg font-semibold">Company Assets</h4>
            <div className="flex flex-col items-center w-full">
              <label htmlFor="org-banner" className="mb-2 text-base font-semibold text-gray-700">Banner</label>
              <ImageCropProvider>
                <ProfileAvatar
                  croppedImageFile={croppedBannerFile}
                  handleUploadAvatar={handleUploadBanner}
                  isLoading={isBannerLoading}
                  error={bannerError}
                  setCroppedImageFile={setCroppedBannerFile}
                  user_avatar={AVATARGetOrgImageUrl(organizationData?.banner)}
                  user_fullname={organizationData?.name || ""}
                  aspectRatio={13 / 3}
                  outputSize={300 * 3 / 13}
                  borderRadius="5px"
                  maxWidth="300px"
                />
              </ImageCropProvider>
            </div>
            <div className="flex flex-col items-center w-full">
              <label htmlFor="org-avatar" className="mb-2 text-base font-semibold text-gray-700">Avatar</label>
              <ImageCropProvider>
                <ProfileAvatar
                  croppedImageFile={croppedImageFile}
                  handleUploadAvatar={handleUploadAvatar}
                  isLoading={isAvatarLoading}
                  error={avatarError}
                  setCroppedImageFile={setCroppedImageFile}
                  user_avatar={AVATARGetOrgImageUrl(organizationData?.avatar)}
                  user_fullname={organizationData?.name || ""}
                  aspectRatio={1 / 1}
                  outputSize={250}
                  borderRadius="50%"
                  maxWidth="140px"
                />
              </ImageCropProvider>
            </div>
          </div>

          <form className="max-xl:!mt-0 xl:mt-0 flex-grow">
            <h4 className="mt-6 w-full text-lg font-semibold">General Information</h4>

            <div className="half-field">
              <InputLabel htmlFor="company-name">{t("company-name")}</InputLabel>
              <Input
                placeholder={t("company-name")}
                disableUnderline
                fullWidth
                id="company-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={!!nameError}
              />
              <div className="message-errors">
                <p className="error">{nameError}</p>
              </div>
            </div>
            <div className="half-field">
              <InputLabel htmlFor="contact-email">{t("contact-email")}</InputLabel>
              <Input
                placeholder={t("contact-email")}
                type="email"
                disableUnderline
                fullWidth
                id="contact-email"
                value={contactEmail}
                onChange={(e) => validateEmailOnChange(e.target.value)}
                error={!!emailError}
              />
              <div className="message-errors">
                <p className="error">{emailError}</p>
              </div>
            </div>

            <h4 className="mt-6 w-full text-lg font-semibold">Billing</h4>

            <div className="half-field">
              <InputLabel htmlFor="invoicing-name">{t("invoicing-name")}</InputLabel>
              <Input
                placeholder={t("invoicing-name")}
                disableUnderline
                fullWidth
                id="invoicing-name"
                value={invoicingName}
                onChange={(e) => setInvoicingName(e.target.value)}
              />
            </div>
            <div className="half-field">
              <InputLabel htmlFor="invoicing-address">{t("invoicing-address")}</InputLabel>
              <Input
                placeholder={t("invoicing-address")}
                disableUnderline
                fullWidth
                id="invoicing-address"
                value={invoicingAddress}
                onChange={(e) => setInvoicingAddress(e.target.value)}
              />
            </div>

            <div className="one-third-field">
              <InputLabel htmlFor="invoicing-city">{t("invoicing-city")}</InputLabel>
              <Input
                placeholder={t("invoicing-city")}
                disableUnderline
                fullWidth
                id="invoicing-city"
                value={invoicingCity}
                onChange={(e) => setInvoicingCity(e.target.value)}
              />
            </div>
            <div className="one-third-field">
              <InputLabel htmlFor="invoicing-zip">{t("invoicing-zip")}</InputLabel>
              <Input
                placeholder={t("invoicing-zip")}
                disableUnderline
                fullWidth
                id="invoicing-zip"
                value={invoicingZip}
                onChange={(e) => setInvoicingZip(e.target.value)}
              />
            </div>
            <div className="!max-w-[210px] one-third-field custom-text-[poppins-400-16px]">
                <label htmlFor="country" className="mb-[12px] block text-lg custom-text-[poppins-500-16px] text-[#111827]">
                  {t("country")}
                </label>
              <div className="relative bg-white">
                <select
                  id="invoicing-country"
                  value={invoicingCountry}
                  onChange={(e) => setInvoicingCountry(e.target.value)}
                  className="px-4 py-3 w-full bg-white rounded-[15px] appearance-none focus:outline-none shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] h-[56px] capitalize"
                >
                  <option value="">{t("select-your-country")}</option>
                  {
                    all_countries.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))
                  }
                </select>
                <div className="flex absolute inset-y-0 right-0 items-center px-4 text-gray-500 pointer-events-none">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </div>
            </div>

            <div className="half-field">
              <InputLabel htmlFor="invoicing-vat-code">{t("invoicing-vat-code")}</InputLabel>
              <Input
                placeholder={t("invoicing-vat-code")}
                disableUnderline
                fullWidth
                id="invoicing-vat-code"
                value={invoicingVATCode}
                onChange={(e) => setInvoicingVATCode(e.target.value)}
              />
            </div>

            <div className="half-field">
              <label htmlFor="language" className="mb-[16px] block text-lg custom-text-[poppins-500-16px] text-[#111827]">
                {t("language")}
              </label>
              <div className="relative bg-white">
                <select
                  id="language"
                  value={defaultLanguage}
                  onChange={(e) => setDefaultLanguage(e.target.value)}
                  className="px-4 py-3 w-full bg-white rounded-lg appearance-none focus:outline-none shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] h-[56px]"
                >
                  <option value="" disabled>{t("select-language")}</option>
                  <option key="en" value="en">English</option>
                  <option key="fr" value="fr">French</option>
                </select>
                <div className="flex absolute inset-y-0 right-0 items-center px-4 text-gray-500 pointer-events-none">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </div>
            </div>


            <div className="mt-6">
              {isSubmitLoading ? (
                <div>{t("saving...")}</div>
              ) : (
                <>
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    className="hover:!bg-blue-dark"
                    disabled={updateOrganizationMutation.isPending}
                  >
                    <h4 className="text">{t("save-changes")}</h4>
                  </Button>
                  <div
                    className="mt-2 message-feedback"
                  >
                    <div className="error">{submissionError}</div>
                    {submissionSuccess && (
                      <div className="success">{t("company-successfully-updated")}</div>
                    )}
                  </div>
                </>
              )}
            </div>
          </form>
          <div className="hidden xl:flex flex-col gap-[30px] items-center justify-center w-fit">
            <h4 className="mt-6 w-full text-lg font-semibold">Company Assets</h4>
            <div className="flex flex-col items-center w-full">
              <label htmlFor="org-banner" className="mb-2 text-base font-semibold text-gray-700">Banner</label>
              <ImageCropProvider>
                <ProfileAvatar
                  croppedImageFile={croppedBannerFile}
                  handleUploadAvatar={handleUploadBanner}
                  isLoading={isBannerLoading}
                  error={bannerError}
                  setCroppedImageFile={setCroppedBannerFile}
                  user_avatar={AVATARGetOrgImageUrl(organizationData?.banner)}
                  user_fullname={organizationData?.name || ""}
                  aspectRatio={13 / 3}
                  outputSize={300 * 3 / 13}
                  borderRadius="5px"
                  maxWidth="300px"
                />
              </ImageCropProvider>
            </div>
            <div className="flex flex-col items-center w-full">
              <label htmlFor="org-avatar" className="mb-2 text-base font-semibold text-gray-700">Avatar</label>
              <ImageCropProvider>
                <ProfileAvatar
                  croppedImageFile={croppedImageFile}
                  handleUploadAvatar={handleUploadAvatar}
                  isLoading={isAvatarLoading}
                  error={avatarError}
                  setCroppedImageFile={setCroppedImageFile}
                  user_avatar={AVATARGetOrgImageUrl(organizationData?.avatar)}
                  user_fullname={organizationData?.name || ""}
                  aspectRatio={1 / 1}
                  outputSize={250}
                  borderRadius="50%"
                  maxWidth="140px"
                  maxHeight="140px"
                />
              </ImageCropProvider>
            </div>
          </div>
        </div>
      </>
    </section>
  );
};

export default CompanyDetailsPage;
